// ============================================
// HOOK: useAuth
// ============================================
// Archivo: src/hooks/useAuth.js

import { useState, useEffect, useCallback } from 'react';
import { authService } from '../services/api';
import { cacheClear } from '../utils/apiCache';
import { toast } from 'react-hot-toast';
import { usePushNotifications } from './usePushNotifications';

const validatePassword = (password) => {
  const hasLetter = /[a-zA-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasMinLength = password && password.length >= 8;

  return {
    isValid: hasLetter && hasNumber && hasMinLength,
    hasLetter,
    hasNumber,
    hasMinLength,
    message: !hasMinLength
      ? 'La contraseña debe tener al menos 8 caracteres'
      : !hasLetter
      ? 'La contraseña debe contener al menos una letra'
      : !hasNumber
      ? 'La contraseña debe contener al menos un número'
      : ''
  };
};

export const useAuth = () => {
  const [auth, setAuth] = useState({
    logged: false,
    user: null
  });
  const [loading, setLoading] = useState(true);
  const { subscribe: subscribePush, unsubscribe: unsubscribePush } = usePushNotifications();

  // Recuperar sesión desde cookie httpOnly al cargar
  useEffect(() => {
    authService.me()
      .then(data => {
        setAuth({ logged: true, user: data.user });
      })
      .catch(() => {
        setAuth({ logged: false, user: null });
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const data = await authService.login(email, password);
      // Llamar a /me para obtener el usuario completo (incluye privacy_accepted_at, etc.)
      let user = data.user;
      try {
        const meData = await authService.me();
        user = meData.user;
      } catch { /* si falla /me usar el user del login */ }

      setAuth({ logged: true, user });

      toast.success(`Bienvenido, ${user.name}`);

      // Suscribir a push notifications en segundo plano
      subscribePush();

      return { success: true, forcePasswordChange: user.force_password_change === 1 };
    } catch (err) {
      toast.error(err.message || 'Credenciales inválidas');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changePassword = useCallback(async (newPassword, currentPassword = null, skipValidation = false) => {
    if (!skipValidation) {
      const validation = validatePassword(newPassword);
      if (!validation.isValid) {
        toast.error(validation.message);
        return { success: false, error: validation.message };
      }
    }
    setLoading(true);
    try {
      await authService.changePassword(newPassword, currentPassword);

      const updatedUser = { ...auth.user, force_password_change: 0 };
      setAuth(prev => ({ ...prev, user: updatedUser }));

      toast.success('Contraseña actualizada correctamente');
      return { success: true };
    } catch (err) {
      toast.error(err.message || 'Error al cambiar contraseña');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [auth.user]);

  const forgotPassword = useCallback(async (email) => {
    setLoading(true);
    try {
      await authService.forgotPassword(email);
      toast.success('Si el correo existe, recibirás una contraseña temporal por email.', { duration: 6000 });
      return { success: true };
    } catch (err) {
      toast.error(err.message || 'Error al procesar solicitud');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      unsubscribePush();
      await fetch(`${process.env.REACT_APP_API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (e) {
      // silenciar error — limpiar estado igualmente
    } finally {
      cacheClear();
      localStorage.removeItem('selectedCompany');
      setAuth({ logged: false, user: null });
      setLoading(false);
      toast('Sesión cerrada', { icon: 'ℹ️' });
    }
  }, [unsubscribePush]);

  const updateUser = useCallback((patch) => {
    setAuth(prev => ({ ...prev, user: { ...prev.user, ...patch } }));
  }, []);

  return {
    auth,
    loading,
    login,
    logout,
    changePassword,
    forgotPassword,
    updateUser,
    isAuthenticated: auth.logged,
    user: auth.user
  };
};
